import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PolicyTimelineVisualization = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [timeRange, setTimeRange] = useState('30days');

  const categories = [
    { id: 'all', label: 'All Categories', icon: 'Layers' },
    { id: 'cardiovascular', label: 'Cardiovascular', icon: 'Heart' },
    { id: 'orthopedic', label: 'Orthopedic', icon: 'Bone' },
    { id: 'surgical', label: 'Surgical', icon: 'Scissors' },
    { id: 'diagnostic', label: 'Diagnostic', icon: 'Activity' }
  ];

  const timeRanges = [
    { id: '7days', label: '7 Days' },
    { id: '30days', label: '30 Days' },
    { id: '90days', label: '90 Days' },
    { id: '1year', label: '1 Year' }
  ];

  const policyEvents = [
    {
      id: 1,
      date: "2026-01-27",
      time: "14:32",
      category: "cardiovascular",
      title: "Medicare Coverage Update - Cardiac Stents",
      description: "CMS updated coverage criteria for drug-eluting stents in high-risk patients. New documentation requirements effective March 1, 2026.",
      impact: "high",
      affectedProducts: 12,
      payer: "Medicare",
      status: "active"
    },
    {
      id: 2,
      date: "2026-01-26",
      time: "09:15",
      category: "orthopedic",
      title: "Blue Cross Policy Revision - Hip Implants",
      description: "Updated medical necessity criteria for total hip arthroplasty. Age restrictions modified for specific patient populations.",
      impact: "medium",
      affectedProducts: 8,
      payer: "Blue Cross Blue Shield",
      status: "active"
    },
    {
      id: 3,
      date: "2026-01-25",
      time: "16:45",
      category: "surgical",
      title: "Aetna Prior Authorization Change",
      description: "New prior authorization requirements for robotic-assisted surgical systems. 30-day implementation period announced.",
      impact: "high",
      affectedProducts: 15,
      payer: "Aetna",
      status: "pending"
    },
    {
      id: 4,
      date: "2026-01-24",
      time: "11:20",
      category: "diagnostic",
      title: "United Healthcare Imaging Policy",
      description: "Coverage expansion for advanced diagnostic imaging devices. Includes new CPT codes and reimbursement rates.",
      impact: "low",
      affectedProducts: 6,
      payer: "United Healthcare",
      status: "active"
    },
    {
      id: 5,
      date: "2026-01-23",
      time: "13:50",
      category: "cardiovascular",
      title: "Cigna Reimbursement Rate Update",
      description: "Quarterly reimbursement rate adjustments for cardiac monitoring devices. Effective February 1, 2026.",
      impact: "medium",
      affectedProducts: 10,
      payer: "Cigna",
      status: "active"
    }
  ];

  const filteredEvents = selectedCategory === 'all' 
    ? policyEvents 
    : policyEvents?.filter(event => event?.category === selectedCategory);

  const getImpactColor = (impact) => {
    switch(impact) {
      case 'high': return 'text-destructive bg-destructive/10 border-destructive/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    return status === 'active' ? 'CheckCircle2' : 'Clock';
  };

  return (
    <section className="bg-background py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Interactive Policy Timeline
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Visualize policy changes across time with real-time filtering and impact analysis
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="flex-1">
              <label className="block text-sm font-medium text-foreground mb-3">Device Category</label>
              <div className="flex flex-wrap gap-2">
                {categories?.map((category) => (
                  <button
                    key={category?.id}
                    onClick={() => setSelectedCategory(category?.id)}
                    className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon name={category?.icon} size={16} />
                    <span className="whitespace-nowrap">{category?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="lg:w-64">
              <label className="block text-sm font-medium text-foreground mb-3">Time Range</label>
              <div className="grid grid-cols-2 gap-2">
                {timeRanges?.map((range) => (
                  <button
                    key={range?.id}
                    onClick={() => setTimeRange(range?.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      timeRange === range?.id
                        ? 'bg-secondary text-secondary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {range?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            {filteredEvents?.map((event, index) => (
              <div 
                key={event?.id}
                className="relative pl-8 md:pl-12 pb-6 md:pb-8 border-l-2 border-border last:border-l-0 last:pb-0"
              >
                <div className="absolute left-0 top-0 w-4 h-4 md:w-5 md:h-5 -ml-2 md:-ml-2.5 bg-primary rounded-full border-4 border-background"></div>
                
                <div className="bg-muted/50 rounded-lg p-4 md:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getImpactColor(event?.impact)}`}>
                          <Icon name="AlertCircle" size={12} />
                          {event?.impact?.toUpperCase()} IMPACT
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-card border border-border text-foreground">
                          <Icon name={getStatusIcon(event?.status)} size={12} />
                          {event?.status?.toUpperCase()}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                        {event?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">
                        {event?.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Building2" size={14} />
                          {event?.payer}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Package" size={14} />
                          {event?.affectedProducts} Products Affected
                        </span>
                      </div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="text-sm font-medium text-foreground whitespace-nowrap">{event?.date}</div>
                      <div className="text-xs text-muted-foreground whitespace-nowrap">{event?.time}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
                      View Full Policy
                    </Button>
                    <Button variant="ghost" size="sm" iconName="Download" iconPosition="left">
                      Export Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Showing {filteredEvents?.length} of {policyEvents?.length} policy events
            </p>
            <Button variant="outline" iconName="RefreshCw" iconPosition="left">
              Load More Events
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PolicyTimelineVisualization;